import { FileBeingAdded } from 'service/file/types';
import {
  BlockManagerAddContactRequest,
  BlockManagerCancelScheduledVisitRequest,
  BlockManagerGetFlatAssessmentSummariesRequest,
  BlockManagerGetFlatCommunicationsRequest,
  BlockManagerGetFlatRequest,
  BlockManagerGetFlatsRequest,
  BlockManagerGetPropertiesRequest,
  BlockManagerGetScheduledVisitCommunicationScheduleRequest,
  BlockManagerGetScheduledVisitRecipientsRequest,
  BlockManagerGetScheduledVisitsRequest,
  BlockManagerListContactsRequest,
  BlockManagerPatchPropertySettingsRequest,
  BlockManagerPostScheduledVisitRequest,
  BlockManagerSendScheduledVisitMessageNowRequest,
  BlockManagerUpdateContactRequest,
  BlockManagerUpdateFlatScheduledVisitAvailabilityRequest,
  EngageContactEvacuationInfo,
  EngageContactInteractionLog,
  EngageFlatIsCommerciallyOwned,
  EngageFlatIsHMO,
  EngageScheduledVisitStatus,
  ScheduledVisitCommunicationSchedule,
  ScheduledVisitMessageTemplate,
} from 'types/DTOs/BlockManagerDTOs';
import {
  EngageFlatContact,
  EngageFlatListItem,
  EngageFlatsFilter,
  EngageInspector,
  EngagePropertiesFilter,
  EngagePropertyListItem,
  EngageStore,
  CommunicationType,
} from 'types/Engage';
import { createAction } from 'typesafe-actions';

export const eventTypeEngageContactAdded = createAction(
  'eventTypeEngageContactAdded',
)<{
  contactId: string;
}>();

export const doEngageClearData = createAction('doEngageClearData')<{
  key: string;
}>();

export const doEngageSetLoadState = createAction('doEngageSetLoadState')<{
  key: string;
  loading: boolean;
  section: keyof Pick<
    EngageStore['data'],
    'properties' | 'flats' | 'scheduledVisits'
  >;
}>();

/**
 * Properties
 */

export const doEngagePropertiesLoad = createAction('doEngagePropertiesLoad')<{
  key: string;
  page: 'reset' | 'next';
}>();

export const doEngagePropertiesSetData = createAction(
  'doEngagePropertiesSetData',
)<{
  key: string;
  response: BlockManagerGetPropertiesRequest['response'];
  clearData: boolean;
  pageNumber: number;
}>();

export const doEngagePropertiesSetFilter = createAction(
  'doEngagePropertiesSetFilter',
)<{
  key: string;
  data: Partial<EngagePropertiesFilter>;
  replace?: boolean;
}>();

/**
 * Flats
 */

export const doEngageFlatsLoad = createAction('doEngageFlatsLoad')<{
  propertyId: string;
  key: string;
  page: 'reset' | 'next';
}>();

export const doEngageFlatsSetFilter = createAction('doEngageFlatsSetFilter')<{
  key: string;
  data: Partial<EngageFlatsFilter>;
  replace?: boolean;
}>();

export const doEngageOnboard = createAction('doEngageOnboard')<{
  propertyId: string;
  key: string;
}>();

export const doEngageFlatsSetData = createAction('doEngageFlatsSetData')<{
  key: string;
  response: BlockManagerGetFlatsRequest['response'];
  clearData: boolean;
  pageNumber: number;
}>();

export const doEngageFlatsUnload = createAction('doEngageFlatsUnload')<{
  key: string;
}>();

export const doOpenPropertySettingsModal = createAction(
  'doOpenPropertySettingsModal',
)<{
  propertyId: string;
  pageKey: string;
}>();

export const doEngagePropertySettingsSave = createAction(
  'doEngagePropertySettingsSave',
)<{
  propertyId: string;
  body: BlockManagerPatchPropertySettingsRequest['body'];
}>();

export const doEngageUploadContacts = createAction('doEngageUploadContacts')<{
  key: string;
  propertyId: string;
  fileName: string;
  file: FileBeingAdded;
}>();

export const doEngageDownloadFlatReferences = createAction(
  'doEngageDownloadFlatReferences',
)<{
  key: string;
  propertyId: string;
}>();

export const doEngageScheduledVisitSave = createAction(
  'doEngageScheduledVisitSave',
)<{
  propertyId: string;
  pageKey: string;
  timeslots: BlockManagerPostScheduledVisitRequest['body']['timeslots'];
  scheduledVisitId?: string;
  status: EngageScheduledVisitStatus;
  inspectorIds: string[];
  currentStatus: EngageScheduledVisitStatus;
}>();

export const doEngageScheduledVisitDelete = createAction(
  'doEngageScheduledVisitDelete',
)<{
  propertyId: string;
  pageKey: string;
  scheduledVisitId: string;
}>();

export const doEngageScheduledVisitCancel = createAction(
  'doEngageScheduledVisitCancel',
)<{
  propertyId: string;
  pageKey: string;
  scheduledVisitId: string;
  cancelledReason: BlockManagerCancelScheduledVisitRequest['body']['cancelledReason'];
}>();

export const doEngageScheduledVisitMessageSendNow = createAction(
  'doEngageScheduledVisitMessageSendNow',
)<{
  propertyId: string;
  pageKey: string;
  scheduledVisitId: string;
  templateType: BlockManagerSendScheduledVisitMessageNowRequest['body']['templateType'];
}>();

export const doEngageScheduledVisitTemplatesLoad = createAction(
  'doEngageScheduledVisitTemplatesLoad',
)<{
  key: string;
}>();

export const doEngageScheduledVisitTemplatesSet = createAction(
  'doEngageScheduledVisitTemplatesSet',
)<{
  key: string;
  templates: ScheduledVisitMessageTemplate[];
}>();

// This is purely for loading from the saga.
export const doEngageScheduledVisitCommunicationScheduleLoad = createAction(
  'doEngageScheduledVisitCommunicationScheduleLoad',
)<{
  key: string;
  propertyUid: BlockManagerGetScheduledVisitCommunicationScheduleRequest['params']['propertyUid'];
  scheduledVisitId: BlockManagerGetScheduledVisitCommunicationScheduleRequest['params']['scheduledVisitId'];
}>();

export const doEngageScheduledVisitCommunicationScheduleUnload = createAction(
  'doEngageScheduledVisitCommunicationScheduleUnload',
)<{
  key: string;
}>();

export const doEngageScheduledVisitCommunicationScheduleSet = createAction(
  'doEngageScheduledVisitCommunicationScheduleSet',
)<{
  key: string;
  communicationSchedule: ScheduledVisitCommunicationSchedule[];
  scheduledVisitId: BlockManagerGetScheduledVisitCommunicationScheduleRequest['params']['scheduledVisitId'];
}>();

export const doEngageScheduledVisitCommunicationScheduleSendNowRequested =
  createAction('doEngageScheduledVisitCommunicationScheduleSendNowRequested')<{
    key: string;
    templateType: BlockManagerSendScheduledVisitMessageNowRequest['body']['templateType'];
    // Ensure to always set this temp date while in modal
    sendNowRequestedDate: NonNullable<
      ScheduledVisitCommunicationSchedule['sendNowRequestedDate']
    >;
  }>();

export const doEngageDownloadTemplate = createAction(
  'doEngageDownloadTemplate',
)<{
  key: string;
}>();

/**
 * Flat
 */

export const doEngageFlatLoad = createAction('doEngageFlatLoad')<{
  key: string;
  property: EngagePropertyListItem;
  flat: EngageFlatListItem;
}>();

export const doEngageFlatSetData = createAction('doEngageFlatSetData')<{
  key: string;
  property: EngagePropertyListItem;
  flat: EngageFlatListItem;
  response: BlockManagerGetFlatRequest['response'];
}>();

export const doEngageFlatCommunicationsLoad = createAction(
  'doEngageFlatCommunicationsLoad',
)<{
  key: string;
  propertyId: string;
  flatId: string;
  contactId: string | null;
  page: 'reset' | 'next';
}>();

export const doEngageFlatCommunicationsSetData = createAction(
  'doEngageFlatCommunicationsSetData',
)<{
  key: string;
  propertyId: string;
  flatId: string;
  response: BlockManagerGetFlatCommunicationsRequest['response'];
  clearData: boolean;
  pageNumber: number;
  contactId: string | null;
}>();

export const doEngageFlatCommunicationsLoadFailed = createAction(
  'doEngageFlatCommunicationsLoadFailed',
)<{
  key: string;
  propertyId: string;
  flatId: string;
  contactId: string | null;
  page: 'reset' | 'next';
}>();

export const doEngageFlatAssessmentSummariesLoad = createAction(
  'doEngageFlatAssessmentSummariesLoad',
)<{
  key: string;
  propertyId: string;
  flatId: string;
  page: 'reset' | 'next';
}>();

export const doEngageFlatAssessmentSummariesSetData = createAction(
  'doEngageFlatAssessmentSummariesSetData',
)<{
  key: string;
  propertyId: string;
  flatId: string;
  response: BlockManagerGetFlatAssessmentSummariesRequest['response'];
  clearData: boolean;
  pageNumber: number;
}>();

export const doEngageFlatAssessmentSummariesLoadFailed = createAction(
  'doEngageFlatAssessmentSummariesLoadFailed',
)<{
  key: string;
  propertyId: string;
  flatId: string;
  page: 'reset' | 'next';
}>();

export const doEngageFlatUpdateData = createAction('doEngageFlatUpdateData')<{
  key: string;
  occupationStatus?: EngageFlatListItem['occupationStatus'];
  isHMO?: EngageFlatIsHMO;
  isCommerciallyOwned?: EngageFlatIsCommerciallyOwned;
  issues?: EngageFlatListItem['issues'];
  archivedContactIds?: string[];
  unarchivedContactIds?: string[];
  contacts?: BlockManagerGetFlatRequest['response']['contacts'];
}>();

type EngageFlatScheduledVisitAvailabilityPayload =
  BlockManagerUpdateFlatScheduledVisitAvailabilityRequest['body'];

export const doEngageFlatScheduledVisitAvailabilitySave = createAction(
  'doEngageFlatScheduledVisitAvailabilitySave',
)<
  {
    key: string;
    propertyId: string;
    flatId: string;
    scheduledVisitId: string;
  } & EngageFlatScheduledVisitAvailabilityPayload
>();

export const doEngageFlatScheduledVisitAvailabilitySet = createAction(
  'doEngageFlatScheduledVisitAvailabilitySet',
)<
  {
    key: string;
    flatId: string;
    scheduledVisitId: string;
  } & EngageFlatScheduledVisitAvailabilityPayload
>();

export const doEngageFlatChangeOccupationStatus = createAction(
  'doEngageFlatChangeOccupationStatus',
)<{
  key: string;
}>();

export const doEngageFlatChangeIsHMO = createAction('doEngageFlatChangeIsHMO')<{
  key: string;
}>();

export const doEngageFlatChangeIsCommerciallyOwned = createAction(
  'doEngageFlatChangeIsCommerciallyOwned',
)<{
  key: string;
}>();

/**
 * Contacts
 */
export const doEngageFlatArchivedContactsLoad = createAction(
  'doEngageFlatArchivedContactsLoad',
)<{
  key: string;
  propertyId: string;
  flatId: string;
}>();

export const doEngageFlatArchivedContactsSet = createAction(
  'doEngageFlatArchivedContactsSet',
)<{
  key: string;
  contacts: BlockManagerListContactsRequest['response']['contacts'];
}>();

export const doEngageArchiveContact = createAction('doEngageArchiveContact')<{
  key: string;
  propertyId: string;
  flatId: string;
  contactId: string;
}>();

export const doEngageUnarchiveContact = createAction(
  'doEngageUnarchiveContact',
)<{
  key: string;
  propertyId: string;
  flatId: string;
  contactId: string;
}>();

export const doEngageSaveContact = createAction('doEngageSaveContact')<{
  key: string;
  propertyId: string;
  flatId: string;
  isNew: boolean;
  contact: Omit<EngageFlatContact, 'hasSupportNeeds' | 'interactionLogs'>;
  actionAfterSave?: 'openEmergencyEvacuationDetails';
}>();

export const doEngageContactEvacuationInfoLoad = createAction(
  'doEngageContactEvacuationInfoLoad',
)<{
  key: string;
  propertyId: string;
  flatId: string;
  contactId: string;
  contactName: string;
}>();

export const doEngageContactEvacuationInfoSave = createAction(
  'doEngageContactEvacuationInfoSave',
)<{
  key: string;
  propertyId: string;
  flatId: string;
  contactId: string;
  info: EngageContactEvacuationInfo;
}>();

export const doEngageContactInteractionLogAdd = createAction(
  'doEngageContactInteractionLogAdd',
)<{
  key: string;
  flatId: string;
  contactId: string;
  log: EngageContactInteractionLog;
}>();

export const doEngageContactUpdate = createAction('doEngageContactUpdate')<{
  key: string;
  isNew: boolean;
  propertyId: string;
  flatId: string;
  contact:
    | BlockManagerAddContactRequest['response']['contact']
    | BlockManagerUpdateContactRequest['response']['contact'];
}>();

/**
 * Communication
 */

export const doEngageSendCommunication = createAction(
  'doEngageSendCommunication',
)<{
  key: string;
  propertyId: string;
  communicationId: string;
  flatId: string;
  contactIds: string[];
}>();

export const doEngagePreviewCommunication = createAction(
  'doEngagePreviewCommunication',
)<{
  key: string;
  propertyId: string;
  flatId: string;
  communicationId: string;
  communicationName: string;
  communicationType: CommunicationType;
}>();

export const doEngageViewCommunicationUpdates = createAction(
  'doEngageViewCommunicationUpdates',
)<{
  communicationId: string;
  communicationName: string;
}>();

export const doEngageSendCommunicationBulk = createAction(
  'doEngageSendCommunicationBulk',
)<{
  key: string;
  propertyId: string;
  communicationId: string;
  flatIds: string[];
  residents: boolean;
  owners: boolean;
  primaryContactsOnly?: boolean;
}>();

/**
 * Scheduled Visits
 */

export const doEngageScheduledVisitsLoad = createAction(
  'doEngageScheduledVisitsLoad',
)<{
  key: string;
  propertyId: string;
}>();

export const doEngageScheduledVisitsUnload = createAction(
  'doEngageScheduledVisitsUnload',
)<{
  key: string;
}>();

export const doEngageScheduledVisitsSetData = createAction(
  'doEngageScheduledVisitsSetData',
)<{
  key: string;
  response: BlockManagerGetScheduledVisitsRequest['response'];
}>();

export const doEngageScheduledVisitRecipientsLoad = createAction(
  'doEngageScheduledVisitRecipientsLoad',
)<{
  key: string;
  propertyId: string;
}>();

export const doEngageScheduledVisitRecipientsUnload = createAction(
  'doEngageScheduledVisitRecipientsUnload',
)<{
  key: string;
}>();

export const doEngageScheduledVisitRecipientsSetData = createAction(
  'doEngageScheduledVisitRecipientsSetData',
)<{
  key: string;
  data: BlockManagerGetScheduledVisitRecipientsRequest['response'];
}>();

/**
 * Inspector
 */

export const doEngageOpenInspectorSelector = createAction(
  'doEngageOpenInspectorSelector',
)<{
  key: string;
  propertyId: string;
  inspectorIds: string[];
}>();

export const eventTypeInspectorsSelected = createAction(
  'eventTypeInspectorsSelected',
)<{
  eventKey: string;
  accounts: EngageInspector[];
}>();

export const eventTypeScheduledVisitAdded = createAction(
  'eventTypeScheduledVisitAdded',
)<{
  scheduledVisitId: string;
}>();
